import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight, MessageSquarePlus } from "lucide-react";
import { StarRating, StarSelect } from "./Stars";
import { getService, postReview } from "../../lib/api";
import { toast } from "sonner";

const card = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] } }),
};

const ServiceCard = ({ s, i, onOpen }) => (
  <motion.button
    custom={i}
    variants={card}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-60px" }}
    onClick={() => onOpen(s)}
    data-testid={`service-card-${s.id}`}
    className="group text-left bg-[#0a0a0a] border border-[#27272a] hover:border-[#0055ff]/60 transition-colors duration-400 flex flex-col"
  >
    <div className="relative overflow-hidden aspect-[4/3]">
      <img src={s.image} alt={s.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent opacity-70" />
      <span className="absolute top-4 left-4 font-mono text-xs uppercase tracking-[0.15em] px-2.5 py-1 border border-white/20 bg-black/40 backdrop-blur-md text-white/80">
        {s.type === "addon" ? "Add-on" : "Package"}
      </span>
      <span className="absolute bottom-4 right-4 font-mono text-2xl text-white font-medium">{s.price}</span>
    </div>
    <div className="p-6 flex flex-col gap-3 flex-1">
      <h3 className="font-display text-2xl tracking-tight">{s.name}</h3>
      <p className="text-white/50 text-sm leading-relaxed line-clamp-2 flex-1">{s.description}</p>
      <div className="flex items-center justify-between pt-3 border-t border-white/10">
        <div className="flex items-center gap-2">
          <StarRating rating={s.avg_rating} testId={`rating-${s.id}`} />
          <span className="font-mono text-xs text-white/40">
            {s.avg_rating > 0 ? `${s.avg_rating} · ${s.review_count}` : "No reviews"}
          </span>
        </div>
        <span className="flex items-center gap-1 font-mono text-xs uppercase tracking-[0.15em] text-[#3377ff] group-hover:gap-2 transition-all">
          View <ArrowUpRight size={14} />
        </span>
      </div>
    </div>
  </motion.button>
);

const ReviewForm = ({ serviceId, onDone }) => {
  const [rating, setRating] = useState(0);
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return toast.error("Please select a star rating");
    if (!name.trim() || !comment.trim()) return toast.error("Please add your name and a comment");
    setBusy(true);
    try {
      await postReview({ service_id: serviceId, name: name.trim(), rating, comment: comment.trim() });
      toast.success("Thanks for your review!");
      setRating(0); setName(""); setComment("");
      onDone();
    } catch {
      toast.error("Could not submit review. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-[#141414] border border-white/10 p-5" data-testid="review-form">
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#3377ff] flex items-center gap-2 mb-4">
        <MessageSquarePlus size={14} /> Leave a review
      </p>
      <StarSelect value={rating} onChange={setRating} />
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Your name"
        data-testid="review-name"
        className="mt-4 w-full bg-black/40 border border-white/10 focus:border-[#0055ff] outline-none px-4 py-3 text-sm transition-colors"
      />
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="How was your detail?"
        rows={3}
        data-testid="review-comment"
        className="mt-3 w-full bg-black/40 border border-white/10 focus:border-[#0055ff] outline-none px-4 py-3 text-sm resize-none transition-colors"
      />
      <button
        type="submit"
        disabled={busy}
        data-testid="review-submit"
        className="cta-sharp mt-4 w-full bg-[#0055ff] hover:bg-[#3377ff] disabled:opacity-50 text-white py-3 font-mono text-xs uppercase tracking-[0.15em]"
      >
        {busy ? "Submitting…" : "Submit Review"}
      </button>
    </form>
  );
};

const ServiceModal = ({ service, onClose, onBook, onReviewed }) => {
  const [data, setData] = useState(service);

  const refresh = async () => {
    const fresh = await getService(service.id);
    setData(fresh);
    onReviewed();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-start md:items-center justify-center p-0 md:p-6 overflow-y-auto"
        data-testid="service-modal"
      >
        <motion.div
          initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-4xl bg-[#0a0a0a] border border-white/10 my-0 md:my-8"
        >
          <button onClick={onClose} data-testid="modal-close" className="absolute top-4 right-4 z-10 h-10 w-10 flex items-center justify-center bg-black/50 border border-white/15 hover:border-white text-white transition-colors">
            <X size={18} />
          </button>

          <div className="relative h-56 md:h-72 overflow-hidden">
            <img src={data.image} alt={data.name} className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
            <div className="absolute bottom-5 left-6 right-6 flex items-end justify-between">
              <div>
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/60">
                  {data.type === "addon" ? "Add-on service" : "Detailing package"}
                </span>
                <h3 className="font-display text-4xl tracking-tight">{data.name}</h3>
              </div>
              <span className="font-mono text-3xl text-[#3377ff] font-medium">{data.price}</span>
            </div>
          </div>

          <div className="p-6 md:p-8">
            <div className="flex items-center gap-3 mb-5">
              <StarRating rating={data.avg_rating} size={20} />
              <span className="font-mono text-sm text-white/50">
                {data.avg_rating > 0 ? `${data.avg_rating} / 5 · ${data.review_count} review${data.review_count === 1 ? "" : "s"}` : "Be the first to review"}
              </span>
            </div>
            <p className="text-white/60 leading-relaxed md:text-lg">{data.description}</p>

            <button
              onClick={() => { onClose(); onBook(data.id); }}
              data-testid="modal-book-btn"
              className="cta-sharp mt-6 inline-flex items-center gap-2 bg-[#0055ff] hover:bg-[#3377ff] text-white px-7 py-3.5 font-mono text-xs uppercase tracking-[0.15em]"
            >
              Book this service <ArrowUpRight size={15} />
            </button>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10 pt-8 border-t border-white/10">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/40 mb-4">
                  Reviews ({data.reviews?.length || 0})
                </p>
                <div className="flex flex-col gap-4 max-h-72 overflow-y-auto pr-2">
                  {data.reviews?.length ? data.reviews.map((r) => (
                    <div key={r.id} className="border-b border-white/5 pb-4" data-testid="review-item">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{r.name}</span>
                        <StarRating rating={r.rating} size={13} />
                      </div>
                      <p className="text-white/50 text-sm mt-1.5 leading-relaxed">{r.comment}</p>
                    </div>
                  )) : (
                    <p className="text-white/30 text-sm">No reviews yet — your feedback could be the first.</p>
                  )}
                </div>
              </div>
              <ReviewForm serviceId={data.id} onDone={refresh} />
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export const Services = ({ services, onBook, onRefresh }) => {
  const [active, setActive] = useState(null);
  return (
    <section id="services" className="mx-auto max-w-[1500px] px-6 md:px-12 py-24 md:py-32" data-testid="services">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <div className="max-w-xl">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#3377ff]">The Menu</span>
          <h2 className="font-display text-4xl md:text-6xl tracking-tighter mt-4 leading-[0.95]">
            Services & pricing
          </h2>
        </div>
        <p className="text-white/50 max-w-sm text-sm leading-relaxed">
          Tap any service to read the full breakdown, see verified customer ratings, and book your slot.
          Add-ons stack onto any package.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
        {services.map((s, i) => (
          <ServiceCard key={s.id} s={s} i={i} onOpen={setActive} />
        ))}
      </div>

      {active && (
        <ServiceModal
          service={active}
          onClose={() => setActive(null)}
          onBook={onBook}
          onReviewed={onRefresh}
        />
      )}
    </section>
  );
};
